// The seamount names of a world, as the client keeps them (v2-the-deep.md
// §4.3, R2620, R2633).
//
// The hello carries every name (`Hello.named_seamounts`), and a State frame
// carries the names that changed (`State.named_seamounts`): a new name, or
// an empty name for a name an operator deleted. A full frame restates every
// name. A name is public: every client gets every name, in the language of
// the reader.
//
// A seamount is a ridge body of the map (§4.3), and `NamedSeamount.cell` is
// a rock cell of that body. The chart finds the body of a name by that
// cell, so the client needs no copy of the seamount walk of the server.
//
// Everything here is pure data, so a test reads it with no engine. While
// the flag `discovery` is off the wire carries no name, and the store stays
// empty.

import type { Hello, NamedSeamount, State } from './gen/proto/aquaphobia';
import { seamountName as W } from './gen/strings';

export class SeamountStore {
  private readonly byBodyV = new Map<number, NamedSeamount>();
  private listV: NamedSeamount[] = [];
  private revV = 0;

  /** Every named seamount, in body order. */
  get list(): readonly NamedSeamount[] { return this.listV; }

  /** A counter that moves on every change, so a reader packs again. */
  get rev(): number { return this.revV; }

  /** The name of a seamount by its body id, or undefined. */
  byBody(body: number): NamedSeamount | undefined { return this.byBodyV.get(body); }

  /** Takes the names of a hello: a new picture of the world. */
  fromHello(h: Pick<Hello, 'namedSeamounts'>): void {
    this.byBodyV.clear();
    for (const n of h.namedSeamounts ?? []) if (n.name) this.byBodyV.set(n.body, n);
    this.sortList();
    this.revV++;
  }

  /**
   * Takes the names of one State frame and answers whether a name changed.
   * A full frame replaces the list; a delta sets each name it carries and
   * deletes each body it carries with an empty name.
   */
  applyState(s: Pick<State, 'full' | 'namedSeamounts'>): boolean {
    const rows = s.namedSeamounts ?? [];
    if (s.full) {
      if (rows.length === 0 && this.byBodyV.size === 0) return false;
      this.byBodyV.clear();
      for (const n of rows) if (n.name) this.byBodyV.set(n.body, n);
    } else {
      if (rows.length === 0) return false;
      for (const n of rows) {
        if (n.name) this.byBodyV.set(n.body, n);
        else this.byBodyV.delete(n.body);
      }
    }
    this.sortList();
    this.revV++;
    return true;
  }

  private sortList(): void {
    this.listV = [...this.byBodyV.values()].sort((a, b) => a.body - b.body);
  }
}

/**
 * The seamount of a list whose label cell lies in a ridge body, or
 * undefined. `cells` is the body in ascending cell order, as
 * `render/terrain-features.ts` writes it.
 */
export function seamountOnBody(
  cells: readonly number[], list: readonly NamedSeamount[],
): NamedSeamount | undefined {
  for (const n of list) if (holds(cells, n.cell)) return n;
  return undefined;
}

/** Does a list in ascending order hold a value? */
function holds(sorted: readonly number[], v: number): boolean {
  let lo = 0, hi = sorted.length - 1;
  while (lo <= hi) {
    const mid = (lo + hi) >> 1;
    const x = sorted[mid];
    if (x === v) return true;
    if (x < v) lo = mid + 1;
    else hi = mid - 1;
  }
  return false;
}

/** Every name of the word list, in the reader's language, built once. */
let wordNames: Set<string> | null = null;

/**
 * Is a name a name of the word list of `[ui.seamount_name]`? A word-list
 * name holds no words of a player, so it carries no REPORT mark, as a hull
 * with a faction pool name carries none (R1886).
 */
export function isWordName(name: string): boolean {
  if (wordNames === null) {
    wordNames = new Set();
    for (const a of W.first) for (const b of W.second) wordNames.add(W.pattern({ first: a, second: b }));
  }
  return wordNames.has(name);
}
